import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { CourseManagementService } from '../../course-add-edit/services/course.service';
import { CourseMasterService } from '../services/course-master.service';
import { ICourse } from '../interface/course-master.interface';
import { IGetCodes, IGetCodesName } from 'src/app/interfaces/codes';
import { UserAuthDetails } from 'src/app/Services/user-sessions/user-auth-details';

@Component({
  selector: 'app-clone-course-modal',
  templateUrl: './clone-course-modal.component.html',
  styleUrls: ['./clone-course-modal.component.css'],
})
export class CloneCourseModalComponent implements OnInit {
  @Input() loggedInUserId = '';
  @Input() sourceExamAgencyId: string | number = '';
  @Input() sourceExamAgencyName = '';
  @Input() sourceCourseMasterId: string | number = '';
  @Input() sourceCourseName = '';
  @Input() sourceCourse: ICourse | null = null;

  examAgencies: IGetCodes[] = [];
  courses: IGetCodes[] = [];
  years: IGetCodesName[] = [];

  months = [
    { label: '', value: '' },
    { label: 'January', value: 'January' },
    { label: 'February', value: 'February' },
    { label: 'March', value: 'March' },
    { label: 'April', value: 'April' },
    { label: 'May', value: 'May' },
    { label: 'June', value: 'June' },
    { label: 'July', value: 'July' },
    { label: 'August', value: 'August' },
    { label: 'September', value: 'September' },
    { label: 'October', value: 'October' },
    { label: 'November', value: 'November' },
    { label: 'December', value: 'December' },
  ];

  loadingCourses = false;
  checkingCourseExists = false;
  courseExistsChecked = false;
  canClone = false;

  cloneForm = this.fb.group({
    examAgency: ['', Validators.required],
    course: ['', Validators.required],
    year: ['', Validators.required],
    month: [''],
  });

  constructor(
    private readonly fb: FormBuilder,
    public readonly activeModal: NgbActiveModal,
    private readonly courseMasterService: CourseMasterService,
    private readonly courseManagementService: CourseManagementService,
    private readonly toaster: ToastrService
  ) {}

  ngOnInit(): void {
    if (!this.loggedInUserId) {
      this.loggedInUserId = UserAuthDetails.getUserLoggedInId() || '';
    }

    this.loadExamAgencies();
    this.loadYears();

    this.cloneForm.valueChanges.subscribe(() => {
      this.resetCourseCheck();
    });
  }

  get fromYear(): string {
    return String(this.sourceCourse?.Exam_Year ?? '');
  }

  get fromMonth(): string {
    return String(this.sourceCourse?.Exam_Month ?? '');
  }

  get selectedExamAgency(): IGetCodes | undefined {
    const selectedValue = this.cloneForm.get('examAgency')?.value;
    return this.examAgencies.find((item) => item.Code === selectedValue);
  }

  get selectedCourse(): IGetCodes | undefined {
    const selectedValue = this.cloneForm.get('course')?.value;
    return this.courses.find((item) => item.Code === selectedValue);
  }

  loadExamAgencies() {
    this.courseMasterService.getDropdownList<IGetCodes>('EXAM_AGENCY').subscribe({
      next: (res) => {
        this.examAgencies = [...res];
      },
      error: (err) => console.log(err),
    });
  }

  loadCourses(event?: Event) {
    const selectedValue = event
      ? (event.target as HTMLSelectElement).value
      : this.cloneForm.get('examAgency')?.value;

    this.cloneForm.patchValue({ course: '' });
    this.courses = [];
    this.resetCourseCheck();

    if (!selectedValue) {
      return;
    }

    this.loadingCourses = true;
    this.courseMasterService
      .getDropdownList<IGetCodes>(`COURSE_MASTER@${selectedValue}`)
      .subscribe({
        next: (res) => {
          this.courses = [...res];
          this.loadingCourses = false;
        },
        error: (err) => {
          this.loadingCourses = false;
          console.log(err);
        },
      });
  }

  loadYears() {
    this.courseMasterService.getDropdownList<IGetCodesName>('Year').subscribe({
      next: (res) => {
        this.years = [...res].sort((a, b) => Number(b.Code) - Number(a.Code));
      },
      error: (err) => console.log(err),
    });
  }

  cancel() {
    this.activeModal.dismiss('Cancel click');
  }

  checkIfCourseExists() {
    this.checkIfCourseExistsAndOptionallyClone(false);
  }

  cloneCourse() {
    this.checkIfCourseExistsAndOptionallyClone(true);
  }

  private checkIfCourseExistsAndOptionallyClone(performClone: boolean) {
    if (this.checkingCourseExists) {
      return;
    }

    this.cloneForm.markAllAsTouched();

    if (
      this.cloneForm.get('examAgency')?.invalid ||
      this.cloneForm.get('course')?.invalid ||
      this.cloneForm.get('year')?.invalid
    ) {
      this.toaster.error('Please select Exam Agency, Course and Year.', 'Error');
      return;
    }

    const payload = {
      LoggedInUser: this.loggedInUserId,
      SessionLanguageTypeCode: 'ENG',
      FromCourseID: Number(this.sourceCourse?.Course_Id || 0),
      ToExamAgencyID: Number(this.cloneForm.get('examAgency')?.value),
      ToCourseMasterID: Number(this.cloneForm.get('course')?.value),
      ToExamYear: Number(this.cloneForm.get('year')?.value),
      ToExamMonth: this.cloneForm.get('month')?.value || null,
    };

    if (!payload.FromCourseID) {
      this.toaster.error('Source course is missing for clone validation.', 'Error');
      return;
    }

    this.checkingCourseExists = true;
    this.courseManagementService.checkCourseExists<any>(payload).subscribe({
      next: (response: any) => {
        this.checkingCourseExists = false;
        this.courseExistsChecked = true;
        const exists = response?.data?.Course_Exists_Ind === 'Y';
        this.canClone = !exists;

        if (exists) {
          const examAgencyName = this.selectedExamAgency?.Description || '';
          const courseName = this.selectedCourse?.Description || '';
          const examYear = String(this.cloneForm.get('year')?.value || '');

          const warningMessage =
            'There is already a course existing for the following:\n' +
            `Exam Agency: ${examAgencyName}\n` +
            `Course: ${courseName}\n` +
            `Exam Year: ${examYear}`;

          this.toaster.warning(warningMessage, 'Warning');
          return;
        }

        this.toaster.success('Course does not exist. You can proceed with Clone.', 'Success');

        if (performClone) {
          this.performClone(payload);
        }
      },
      error: () => {
        this.checkingCourseExists = false;
        this.courseExistsChecked = false;
        this.canClone = false;
      },
    });
  }

  private performClone(payload: any) {
    this.courseManagementService.performCourseCloning<any>(payload).subscribe({
      next: (response) => {
        if (response?.code === 200) {
          this.toaster.success('Course cloned successfully.', 'Success');
          this.activeModal.close({ cloned: true, response });
        } else {
          this.toaster.error(response?.message || 'Course cloning failed.', 'Error');
        }
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  private resetCourseCheck() {
    this.courseExistsChecked = false;
    this.canClone = false;
  }


}
